import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import UserIcon from '@BUP/assets/Icons/user_icon.svg'
import PageLayout from '@BUP/components/Layout/Page'
import TitleDashboard from '@BUP/components/Generics/TitleDashboard'
import BupSpinner from '@BUP/components/Loading/Spinner'
import AlertComponent from '@BUP/components/Generics/Alert'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import ResultsSection from '@BUP/components/CourseAssignment/ResultsSection'
import { useGetCurrentPeriodStatus } from '@BUP/hooks/CourseAssignment/useGetCurrentPeriodStatus'

/**
 * Own screen for a period's assignment results (upload + drag & drop between courses), split out
 * from the period dashboard so the roster/exclusions/evaluations workflow and the results review
 * don't compete for space on one long page.
 */
const CourseAssignmentResultsPage = () => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const navigate = useNavigate()
  // Different grades can each have their own period open at once (see the backend's openPeriod
  // doc comment), so "the current period" needs to know which grade the counselor came from -
  // carried here as a query param from the period dashboard's grade selector.
  const [searchParams] = useSearchParams()
  const grade = searchParams.get('grade')

  const { isLoading, error, period } = useGetCurrentPeriodStatus(grade)

  const renderContent = () => {
    if (isLoading) return <BupSpinner size='large' />

    if (error) {
      return (
        <AlertComponent
          type='error'
          messageKey='common.resources.course-assignment.actions.read.failure'
          error={error}
        />
      )
    }

    if (!period) {
      return <AlertComponent type='info' message={t('noPeriod')} />
    }

    // Keyed by periodId so switching grade (only the `?grade=` query param changes, no remount of
    // this page) resets ResultsSection's internal state (selectedVersionId, working copy) instead
    // of carrying over a version id that belongs to the previous grade's period.
    return <ResultsSection key={period.id} periodId={period.id} />
  }

  return (
    <PageLayout>
      <>
        <ButtonComponent
          buttonText={t('backToPeriodButton')}
          colorType='tertiary'
          size='small'
          onClick={() => navigate('/course-assignment/period')}
        />
        <TitleDashboard icon={UserIcon} text={t('resultsSectionTitle')} />
        {renderContent()}
      </>
    </PageLayout>
  )
}

export default CourseAssignmentResultsPage
